import { siteBuilderMediaPresentation } from "../siteBuilderBlockPresentation";
import type { ShopTemplateBlock } from "../types";

export const MORROW_HERO_IMAGE = "/templates/morrow/assets/open-market-hero.webp";

export type MorrowRenderBlock = {
    block: ShopTemplateBlock;
    index: number;
};

function morrowRegion(block: ShopTemplateBlock): string {
    const options = block.options;

    return options && typeof options.morrowRegion === "string"
        ? options.morrowRegion
        : "";
}

/**
 * Previously applied Morrow documents may still contain the original empty
 * hero media leaf. Keep the persisted document editable, but give the public
 * renderer the same real campaign image as newly applied snapshots.
 */
export function withMorrowHeroMediaFallback(
    region: string,
    block: ShopTemplateBlock,
): ShopTemplateBlock {
    if (
        region !== "hero"
        || (block.type !== "image" && block.type !== "media")
        || siteBuilderMediaPresentation(block.content).kind !== "empty"
    ) {
        return block;
    }

    return {
        ...block,
        content: {
            ...(block.content ?? {}),
            image: MORROW_HERO_IMAGE,
        },
    };
}

/**
 * Old Morrow snapshots placed the category directory before the product feed.
 * The compact storefront introduces products immediately after the hero while
 * preserving original block indexes for builder selection and persistence.
 */
export function orderMorrowRenderBlocks(
    blocks: ShopTemplateBlock[],
): MorrowRenderBlock[] {
    const entries = blocks.map((block, index) => ({ block, index }));
    const heroIndex = entries.findIndex(({ block }) => morrowRegion(block) === "hero");
    const categoryIndex = entries.findIndex(({ block }) => morrowRegion(block) === "categories");
    const productFeedIndex = entries.findIndex(({ block }) => morrowRegion(block) === "productFeed");

    if (
        heroIndex < 0
        || categoryIndex < 0
        || productFeedIndex < 0
        || productFeedIndex < categoryIndex
    ) {
        return entries;
    }

    const [productFeed] = entries.splice(productFeedIndex, 1);

    if (!productFeed) {
        return entries;
    }

    const nextCategoryIndex = entries.findIndex(({ block }) => morrowRegion(block) === "categories");
    entries.splice(nextCategoryIndex, 0, productFeed);

    return entries;
}
